/**
 * Teaching Bundles — list / view / public share.
 *
 * Routes:
 *   /teaching/bundles            → list user's saved bundles
 *   /teaching/bundles/:id        → view a saved bundle (owner)
 *   /share/bundle/:share_id      → public shared bundle view
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowLeft, FileText, Loader2, Trash2, Share2, ExternalLink, Sparkles, BookOpen,
  ClipboardList, Clock, Languages, Volume2, Download,
} from "lucide-react";
import PremiumNav from "@/components/premium/PremiumNav";
import PremiumFooter from "@/components/premium/PremiumFooter";
import { PremiumCard, PremiumButton, SectionHeading, Pill } from "@/components/premium/Primitives";

const API = process.env.REACT_APP_BACKEND_URL || '';

/* ─── List page ──────────────────────────────────────────────────────── */
export function BundlesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/teaching/bundles`, { withCredentials: true });
        setItems(r.data?.items || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load bundles");
        setItems([]);
      }
    })();
  }, []);

  const deleteBundle = async (id) => {
    if (!window.confirm("Delete this bundle permanently?")) return;
    try {
      await axios.delete(`${API}/api/teaching/bundles/${id}`, { withCredentials: true });
      setItems((arr) => (arr || []).filter((x) => x.id !== id));
    } catch (_e) { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-white font-sans" data-testid="bundles-list">
      <PremiumNav />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <button
          onClick={() => navigate("/teaching")}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#A855F7] mb-6"
          data-testid="bundles-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Teaching Suite
        </button>

        <SectionHeading
          eyebrow="My saved bundles"
          title="Your AI Assistant library"
          subtitle="Every bundle you've saved — generate once, reuse across classrooms."
        />

        <div className="mt-8">
          {error === "Login required" && (
            <PremiumCard className="p-8 text-center">
              <p className="text-slate-700 mb-4">Please log in to view your saved bundles.</p>
              <Link to="/login" className="text-[#A855F7] font-medium hover:underline">Log in →</Link>
            </PremiumCard>
          )}

          {items === null && (
            <div className="flex items-center justify-center py-12 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin"/>
            </div>
          )}

          {Array.isArray(items) && items.length === 0 && !error && (
            <PremiumCard className="p-8 text-center" data-testid="bundles-empty">
              <Sparkles className="w-8 h-8 text-slate-300 mx-auto mb-3"/>
              <p className="text-sm text-slate-600">No saved bundles yet.</p>
              <PremiumButton className="mt-4" onClick={() => navigate("/teaching/ai-assistant")}>
                Generate your first bundle
              </PremiumButton>
            </PremiumCard>
          )}

          {Array.isArray(items) && items.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-4">
              {items.map((b) => (
                <PremiumCard key={b.id} className="p-5" data-testid={`bundle-card-${b.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <Link to={`/teaching/bundles/${b.id}`} className="font-heading text-lg font-semibold text-[#111111] hover:text-[#A855F7] line-clamp-2">
                      {b.title}
                    </Link>
                    <button onClick={() => deleteBundle(b.id)} className="text-slate-400 hover:text-red-500 shrink-0" data-testid={`bundle-delete-${b.id}`}>
                      <Trash2 className="w-4 h-4"/>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {b.grade && <Pill variant="brand">Grade {b.grade}</Pill>}
                    {b.subject && <Pill variant="neutral">{b.subject}</Pill>}
                    {b.language && b.language !== "english" && (
                      <Pill variant="outline"><Languages className="w-3 h-3 inline -mt-0.5 mr-1"/>{b.language}</Pill>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400">
                    {b.source ? `Source: ${b.source}` : "Pasted text"} ·{" "}
                    {b.created_at ? new Date(b.created_at).toLocaleDateString() : ""}
                  </div>
                  {b.share_id && (
                    <div className="mt-2 text-[11px] text-emerald-700">
                      <Share2 className="w-3 h-3 inline -mt-0.5 mr-1"/> Public link active
                    </div>
                  )}
                </PremiumCard>
              ))}
            </div>
          )}
        </div>
      </div>
      <PremiumFooter />
    </div>
  );
}

/* ─── Single bundle view (owner) ─────────────────────────────────────── */
export function BundleView() {
  const { id } = useParams();
  return <BundleDetail endpoint={`${API}/api/teaching/bundles/${id}`} mode="owner" />;
}

/* ─── Public shared bundle view ──────────────────────────────────────── */
export function PublicBundleView() {
  const { share_id } = useParams();
  return <BundleDetail endpoint={`${API}/api/teaching/bundles/public/${share_id}`} mode="public" />;
}

/* ─── Shared detail renderer ────────────────────────────────────────── */
function BundleDetail({ endpoint, mode }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("lesson_plan");
  const [error, setError] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [audioB64, setAudioB64] = useState("");
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioProvider, setAudioProvider] = useState("sarvam");
  const [audioLang, setAudioLang] = useState("hi-IN");
  const [audioVoice, setAudioVoice] = useState("shreya");
  const [audioMediaType, setAudioMediaType] = useState("audio/wav");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(endpoint, { withCredentials: true });
        setData(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load bundle");
      }
    })();
  }, [endpoint]);

  const generateShare = async () => {
    if (!data?.id) return;
    try {
      const r = await axios.post(`${API}/api/teaching/bundles/${data.id}/share`, {}, { withCredentials: true });
      // Use OG url so WhatsApp/Twitter previews work
      const suffix = r.data?.og_url_suffix || r.data?.url_suffix || `/share/bundle/${r.data?.share_id}`;
      const full = `${window.location.origin}${suffix}`;
      setShareUrl(full);
      try { await navigator.clipboard.writeText(full); } catch (_e) { /* ignore */ }
    } catch (_e) { /* ignore */ }
  };

  const narrate = async () => {
    if (!data?.id || audioLoading || mode !== "owner") return;
    setAudioLoading(true);
    try {
      const r = await axios.post(`${API}/api/teaching/bundles/${data.id}/narrate`, null, {
        params: { voice: audioVoice, provider: audioProvider, language: audioLang },
        withCredentials: true,
        timeout: 120000,
      });
      setAudioB64(r.data?.audio_base64 || "");
      setAudioMediaType(r.data?.media_type || (audioProvider === "sarvam" ? "audio/wav" : "audio/mp3"));
    } catch (_e) { /* ignore */ }
    finally { setAudioLoading(false); }
  };

  const downloadPpt = async () => {
    if (!data?.bundle) return;
    const b = data.bundle || {};
    const lp = b.lesson_plan || {};
    // Build slides from bundle: 1 objectives + 1 per lesson phase + 1 quiz overview + 1 homework
    const slides = [];
    if ((lp.learning_objectives || []).length) {
      slides.push({
        title: "Learning Objectives",
        bullets: (lp.learning_objectives || []).slice(0, 6),
        notes: lp.title || "",
      });
    }
    (lp.lesson_structure || []).forEach((ph) => {
      slides.push({
        title: `${ph.phase || "Phase"} — ${ph.duration_min || 0} min`,
        bullets: [
          ph.activity || "",
          ph.teacher_action ? `Teacher: ${ph.teacher_action}` : "",
          ph.student_action ? `Student: ${ph.student_action}` : "",
        ].filter(Boolean),
        notes: ph.activity || "",
      });
    });
    if ((b.quiz?.mcq || []).length) {
      slides.push({
        title: "Quiz Preview",
        bullets: (b.quiz.mcq || []).slice(0, 5).map((m, i) => `Q${i + 1}. ${m.q}`),
        notes: `${b.quiz.total_marks || ""} marks total`,
      });
    }
    if ((b.homework?.days || []).length) {
      slides.push({
        title: "Homework Plan",
        bullets: (b.homework.days || []).slice(0, 6).map((d) => `Day ${d.day}: ${d.focus || ""}`),
        notes: b.homework.weekly_goal || "",
      });
    }
    if (slides.length === 0) {
      slides.push({ title: data.title || "Lesson", bullets: ["Content not available"] });
    }
    try {
      const r = await axios.post(`${API}/api/gap/ppt/render`, {
        title: data.title || "Lesson",
        subtitle: `${data.grade ? `Grade ${data.grade}` : ""}${data.subject ? " · " + data.subject : ""}`,
        slides,
        theme: "light",
        footer: "Powered by MarkeeMix Teaching AI Assistant",
      });
      if (r.data?.download_url) {
        window.open(`${API}${r.data.download_url}`, "_blank");
      }
    } catch (_e) { /* ignore */ }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-white font-sans">
        <PremiumNav />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold text-[#111111] mb-2">{error}</h1>
          <PremiumButton onClick={() => navigate(mode === "public" ? "/" : "/teaching/bundles")}>
            {mode === "public" ? "Go home" : "Back to bundles"}
          </PremiumButton>
        </div>
        <PremiumFooter />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white font-sans">
        <PremiumNav />
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin"/>
        </div>
        <PremiumFooter />
      </div>
    );
  }

  const b = data.bundle || {};

  return (
    <div className="min-h-screen bg-white font-sans" data-testid={`bundle-detail-${mode}`}>
      <PremiumNav />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {mode === "owner" && (
          <button onClick={() => navigate("/teaching/bundles")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#A855F7] mb-6">
            <ArrowLeft className="w-4 h-4"/> Back to my bundles
          </button>
        )}

        <PremiumCard className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#111111]" data-testid="bundle-title">{data.title}</h1>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.grade && <Pill variant="brand">Grade {data.grade}</Pill>}
                {data.subject && <Pill variant="neutral">{data.subject}</Pill>}
                {data.language && data.language !== "english" && (
                  <Pill variant="outline"><Languages className="w-3 h-3 inline -mt-0.5 mr-1"/>{data.language}</Pill>
                )}
                {mode === "public" && <Pill variant="dark">Shared</Pill>}
              </div>
            </div>

            {mode === "owner" && (
              <div className="flex flex-col items-end gap-2">
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <select
                    value={audioProvider}
                    onChange={(e) => {
                      const p = e.target.value;
                      setAudioProvider(p);
                      setAudioVoice(p === "sarvam" ? "shreya" : "alloy");
                    }}
                    data-testid="bundle-provider"
                    className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white outline-none"
                  >
                    <option value="sarvam">🇮🇳 Indian voices</option>
                    <option value="openai">🌐 English voices</option>
                  </select>
                  {audioProvider === "sarvam" && (
                    <select
                      value={audioLang}
                      onChange={(e) => setAudioLang(e.target.value)}
                      data-testid="bundle-lang"
                      className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white outline-none"
                    >
                      <option value="hi-IN">Hindi</option>
                      <option value="en-IN">English (India)</option>
                      <option value="ta-IN">Tamil</option>
                      <option value="te-IN">Telugu</option>
                      <option value="bn-IN">Bengali</option>
                      <option value="mr-IN">Marathi</option>
                      <option value="gu-IN">Gujarati</option>
                      <option value="kn-IN">Kannada</option>
                      <option value="ml-IN">Malayalam</option>
                      <option value="pa-IN">Punjabi</option>
                      <option value="od-IN">Odia</option>
                    </select>
                  )}
                  <select
                    value={audioVoice}
                    onChange={(e) => setAudioVoice(e.target.value)}
                    data-testid="bundle-voice"
                    className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white outline-none capitalize"
                  >
                    {audioProvider === "sarvam" ? (
                      <>
                        <option value="shreya">Shreya (Female)</option>
                        <option value="priya">Priya (Female)</option>
                        <option value="kavitha">Kavitha (Female)</option>
                        <option value="niharika">Niharika (Female)</option>
                        <option value="simran">Simran (Female)</option>
                        <option value="ritu">Ritu (Female)</option>
                        <option value="shruti">Shruti (Female)</option>
                        <option value="kavya">Kavya (Female)</option>
                        <option value="aditya">Aditya (Male)</option>
                        <option value="rahul">Rahul (Male)</option>
                        <option value="rohan">Rohan (Male)</option>
                        <option value="kabir">Kabir (Male)</option>
                        <option value="shubh">Shubh (Male)</option>
                        <option value="vijay">Vijay (Male)</option>
                      </>
                    ) : (
                      <>
                        <option value="alloy">Alloy</option>
                        <option value="nova">Nova</option>
                        <option value="shimmer">Shimmer</option>
                        <option value="onyx">Onyx</option>
                        <option value="fable">Fable</option>
                      </>
                    )}
                  </select>
                  <button onClick={narrate} disabled={audioLoading} data-testid="bundle-narrate" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                    {audioLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Volume2 className="w-3.5 h-3.5"/>} {audioB64 ? "Re-generate" : "Narrate"}
                  </button>
                  <button onClick={downloadPpt} data-testid="bundle-download-ppt" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50">
                    <Download className="w-3.5 h-3.5"/> Download .pptx
                  </button>
                  <button onClick={generateShare} data-testid="bundle-share" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded-lg hover:bg-slate-50">
                    <Share2 className="w-3.5 h-3.5"/> {shareUrl ? "Copied!" : "Share link"}
                  </button>
                </div>
                {shareUrl && (
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#A855F7] truncate max-w-xs hover:underline" data-testid="bundle-share-url">
                    {shareUrl} <ExternalLink className="w-3 h-3 inline -mt-0.5"/>
                  </a>
                )}
                {audioB64 && (
                  <audio controls src={`data:${audioMediaType};base64,${audioB64}`} className="w-64 mt-1" data-testid="bundle-audio"/>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-2 border-b border-slate-200">
            {[
              { id: "lesson_plan", label: "Lesson Plan", Icon: BookOpen },
              { id: "quiz", label: "Quiz", Icon: ClipboardList },
              { id: "homework", label: "Homework", Icon: Clock },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`bundle-tab-${t.id}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id ? "border-[#A855F7] text-[#A855F7]" : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                <t.Icon className="w-4 h-4"/> {t.label}
              </button>
            ))}
          </div>
        </PremiumCard>

        <motion.div key={tab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-5">
          {tab === "lesson_plan" && <SimpleBlock title={b.lesson_plan?.title} items={[
            ["Duration", `${b.lesson_plan?.duration_min || "—"} min`],
            ["Learning objectives", (b.lesson_plan?.learning_objectives || []).join(" · ")],
            ["Assessment strategy", b.lesson_plan?.assessment_strategy],
          ]} />}
          {tab === "quiz" && (
            <PremiumCard className="p-6">
              <h3 className="font-heading text-xl font-bold text-[#111111]">{b.quiz?.title || "Quiz"}</h3>
              <div className="mt-4 space-y-3">
                {(b.quiz?.mcq || []).map((m, i) => (
                  <div key={i} className="p-4 rounded-xl border border-slate-200">
                    <div className="text-sm font-semibold mb-1">Q{i+1}. {m.q}</div>
                    <ul className="text-sm text-slate-700 space-y-0.5">
                      {(m.options || []).map((o, j) => (
                        <li key={j} className={o.startsWith(m.answer) ? "font-medium text-green-700" : ""}>{o}</li>
                      ))}
                    </ul>
                    <div className="text-xs text-slate-500 mt-1">Answer: {m.answer}</div>
                  </div>
                ))}
                {(b.quiz?.short || []).map((s, i) => (
                  <div key={`s${i}`} className="p-4 rounded-xl border border-slate-200">
                    <div className="text-sm font-semibold mb-1">Short Q{i+1}. {s.q}</div>
                    <div className="text-xs text-slate-500 mt-1">Model answer: {s.answer}</div>
                  </div>
                ))}
              </div>
            </PremiumCard>
          )}
          {tab === "homework" && (
            <PremiumCard className="p-6">
              <h3 className="font-heading text-xl font-bold text-[#111111]">Homework — {b.homework?.total_days || 0} day(s)</h3>
              <div className="mt-4 space-y-3">
                {(b.homework?.days || []).map((d, i) => (
                  <div key={i} className="p-4 rounded-xl border border-slate-200">
                    <div className="font-semibold text-sm mb-1">Day {d.day} — {d.focus}</div>
                    <ul className="text-sm text-slate-700 space-y-1">
                      {(d.tasks || []).map((t, j) => (
                        <li key={j}>• <span className="text-[#A855F7] uppercase text-[10px] font-bold">{t.type}</span> {t.task} <span className="text-xs text-slate-400">({t.time_min}m)</span></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </PremiumCard>
          )}
        </motion.div>

        {mode === "public" && (
          <div className="mt-8 text-center text-xs text-slate-400">
            Generated with <Link to="/" className="text-[#A855F7] hover:underline"><FileText className="w-3 h-3 inline -mt-0.5 mr-0.5"/>MarkeeMix Teaching AI Assistant</Link>
          </div>
        )}
      </div>
      <PremiumFooter />
    </div>
  );
}

function SimpleBlock({ title, items }) {
  return (
    <PremiumCard className="p-6">
      <h3 className="font-heading text-xl font-bold text-[#111111]">{title || "Lesson Plan"}</h3>
      <div className="mt-4 space-y-3">
        {items.filter(([_, v]) => v).map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-400">{k}</div>
            <div className="text-sm text-slate-700 mt-0.5">{v}</div>
          </div>
        ))}
      </div>
    </PremiumCard>
  );
}
